import { useEffect, useState, useCallback } from 'react';
import { useRouter } from 'next/router';
import { onMessage } from 'firebase/messaging';
import { 
    fetchToken, 
    messaging, 
    trackNotificationReceived, 
    trackNotificationClicked, 
    trackNotificationPermissionGranted, 
    trackNotificationPermissionDenied 
} from '../../firebase';
import { notifySuccess, notifyError } from '@utils/toast';

// Constants for better maintainability
const MAX_RETRIES = 3;
const RETRY_DELAYS = [1000, 2000, 4000]; // Exponential backoff delays
const TOKEN_STORAGE_KEY = 'fcm_token_storage';

// Global state to prevent multiple simultaneous hook instances
let globalTokenState = {
    token: null,
    notificationPermissionStatus: null,
    isLoading: false,
    error: null,
    isInitialized: false,
    subscribers: new Set(),
    retryCount: 0
};

// Global refs to prevent multiple simultaneous operations
let isGloballyLoading = false;
let globalAbortController = null;

// Helper functions for global state management
const notifySubscribers = () => {
    globalTokenState.subscribers.forEach(callback => {
        try {
            callback(globalTokenState);
        } catch (error) {
            console.error('Error notifying FCM subscriber:', error);
        }
    });
};

const updateGlobalState = (updates) => {
    Object.assign(globalTokenState, updates);
    notifySubscribers();
};

const addSubscriber = (callback) => {
    globalTokenState.subscribers.add(callback);
    return () => globalTokenState.subscribers.delete(callback);
};

// Helper function to check if notifications are supported
function isNotificationSupported() {
    return typeof window !== 'undefined' && 'Notification' in window && 'serviceWorker' in navigator;
}

// Store token locally for persistence across page reloads
const storeTokenLocally = (fcmToken) => {
    try {
        const tokenData = {
            token: fcmToken,
            timestamp: Date.now(),
            permission: Notification.permission
        };
        localStorage.setItem(TOKEN_STORAGE_KEY, JSON.stringify(tokenData));
    } catch (error) {
        console.warn('Failed to store FCM token locally:', error);
    }
};

// Get stored token
const getStoredToken = () => {
    try {
        const stored = localStorage.getItem(TOKEN_STORAGE_KEY);
        if (stored) {
            const tokenData = JSON.parse(stored);
            // Check if token is less than 7 days old
            const isValid = Date.now() - tokenData.timestamp < 7 * 24 * 60 * 60 * 1000;
            if (isValid && tokenData.permission === 'granted') {
                return tokenData.token;
            }
        }
    } catch (error) {
        console.warn('Failed to get stored FCM token:', error);
    }
    return null;
};

// Global function to send token to server
const sendTokenToServer = async (fcmToken) => {
    // Cancel previous request if it exists
    if (globalAbortController) {
        globalAbortController.abort();
    }

    globalAbortController = new AbortController();

    try {
        const response = await fetch('/api/fcm/save-token', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                token: fcmToken,
                userId: 'anonymous', // Replace with actual user ID from your auth context
                userAgent: navigator.userAgent,
                timestamp: Date.now(),
            }),
            signal: globalAbortController.signal,
        });

        if (response.ok) {
            console.log('FCM token sent to server successfully');
            storeTokenLocally(fcmToken);
            return true;
        } else {
            const errorData = await response.text();
            console.error('Failed to send FCM token to server:', response.status, errorData);
            return false;
        }
    } catch (error) {
        if (error.name === 'AbortError') {
            console.log('Token send request was cancelled');
            return false;
        }
        console.error('Error sending FCM token to server:', error);
        return false;
    }
};

// Helper function to get notification permission and token
async function getNotificationPermissionAndToken() {
    console.log('🔑 FCM: Starting permission and token request...');

    // Step 1: Check if Notifications are supported in the browser
    if (!isNotificationSupported()) {
        console.info("❌ FCM: This browser does not support desktop notifications or service workers");
        return { token: null, error: 'UNSUPPORTED' };
    }

    try {
        console.log('🔍 FCM: Current notification permission:', Notification.permission);
        
        // Step 2: Check if permission is already granted
        if (Notification.permission === "granted") {
            console.log('✅ FCM: Permission already granted, fetching token...');
            const token = await fetchToken();
            console.log('🔍 FCM: Token fetch result:', token ? 'Success' : 'Failed');
            // Track that permission was already granted
            await trackNotificationPermissionGranted();
            return { token, error: null };
        }

        // Step 3: If permission is not denied, request permission from the user
        if (Notification.permission !== "denied") {
            console.log('🙋 FCM: Requesting notification permission from user...');
            const permission = await Notification.requestPermission();
            console.log('🔍 FCM: User permission response:', permission);

            if (permission === "granted") {
                console.log('✅ FCM: Permission granted, fetching token...');
                const token = await fetchToken();
                console.log('🔍 FCM: Token fetch result:', token ? 'Success' : 'Failed');
                // Track permission granted
                await trackNotificationPermissionGranted();
                return { token, error: null };
            } else {
                // Track permission denied
                await trackNotificationPermissionDenied();
            }
        } else {
            // Permission was already denied
            await trackNotificationPermissionDenied();
        }

        console.log("❌ FCM: Notification permission not granted.");
        return { token: null, error: 'PERMISSION_DENIED' };
    } catch (error) {
        console.error('❌ FCM: Error getting notification permission/token:', error);
        return { token: null, error: error.message };
    }
}

// Global function to load token (prevents multiple simultaneous loads)
const globalLoadToken = async () => {
    // Prevent multiple loads
    if (isGloballyLoading) {
        console.log('⏸️ FCM: Global load already in progress, skipping...');
        return;
    }

    console.log('🔄 FCM: Starting global token load process...');
    isGloballyLoading = true;
    
    updateGlobalState({ 
        isLoading: true, 
        error: null 
    });

    try {
        // Check for stored token first
        const storedToken = getStoredToken();
        if (storedToken && Notification.permission === 'granted') {
            updateGlobalState({
                token: storedToken,
                notificationPermissionStatus: 'granted',
                isLoading: false,
                isInitialized: true
            });
            console.log('✅ FCM: Using stored FCM token:', storedToken.substring(0, 20) + '...');
            return;
        }

        console.log('🔑 FCM: Requesting notification permission and token...');
        const { token: fcmToken, error: tokenError } = await getNotificationPermissionAndToken();

        console.log('🔍 FCM: Token result:', fcmToken ? 'Token received' : 'No token');
        console.log('🔍 FCM: Error result:', tokenError || 'No error');

        // Handle various error cases
        if (tokenError === 'UNSUPPORTED') {
            updateGlobalState({
                error: 'Notifications are not supported in this browser',
                isLoading: false,
                isInitialized: true
            });
            console.info("❌ FCM: Push Notifications - not supported");
            return;
        }

        if (tokenError === 'PERMISSION_DENIED' || Notification.permission === "denied") {
            updateGlobalState({
                notificationPermissionStatus: "denied",
                error: 'Notification permission was denied',
                isLoading: false,
                isInitialized: true
            });
            console.info(
                "%c❌ FCM: Push Notifications - permission denied",
                "color: red; background: #ffe6e6; padding: 8px; font-size: 14px"
            );
            return;
        }

        // Retry logic for token fetching
        if (!fcmToken) {
            if (globalTokenState.retryCount >= MAX_RETRIES) {
                const errorMsg = "Unable to enable notifications after multiple retries";
                updateGlobalState({
                    error: errorMsg,
                    isLoading: false,
                    isInitialized: true
                });
                console.error(
                    "%c❌ FCM: Push Notifications - unable to load token after retries",
                    "color: red; background: #ffe6e6; padding: 8px; font-size: 14px"
                );
                notifyError("Unable to enable notifications. Please refresh the page and try again.");
                return;
            }

            globalTokenState.retryCount += 1;
            console.warn(`🔄 FCM: Token fetch failed. Retrying... (${globalTokenState.retryCount}/${MAX_RETRIES})`);

            // Wait before retrying with exponential backoff
            setTimeout(() => {
                isGloballyLoading = false;
                globalLoadToken();
            }, RETRY_DELAYS[globalTokenState.retryCount - 1] || 4000);
            return;
        }

        // Success! Update global state
        updateGlobalState({
            notificationPermissionStatus: Notification.permission,
            token: fcmToken,
            isLoading: false,
            error: null,
            isInitialized: true
        });

        console.log('✅ FCM: FCM Token retrieved successfully:', fcmToken.substring(0, 20) + '...');
        console.log('📋 FCM: FULL TOKEN (copy this):', fcmToken);
        console.log('🔗 FCM: Token length:', fcmToken.length);

        // Store in global variable for easy access
        if (typeof window !== 'undefined') {
            window.fcmToken = fcmToken;
            console.log('💾 FCM: Token stored in window.fcmToken - you can copy it from there');
        }

        // Send token to your backend
        const success = await sendTokenToServer(fcmToken);
        if (success) {
            console.log('✅ FCM: Token saved to server successfully');
            notifySuccess('Notifications enabled successfully!');
        } else {
            console.warn('⚠️ FCM: Token enabled but server sync failed');
            notifyError('Notifications enabled, but failed to sync with server');
        }

    } catch (error) {
        const errorMsg = 'Failed to enable notifications';
        updateGlobalState({
            error: errorMsg,
            isLoading: false,
            isInitialized: true
        });
        console.error('❌ FCM: Error loading FCM token:', error);
        notifyError('Failed to enable notifications. Please try again.');
    } finally {
        isGloballyLoading = false;
        console.log('🏁 FCM: Global load token process completed');
    }
};

// Global request permission function
const globalRequestPermission = async () => {
    if (isGloballyLoading) return globalTokenState.notificationPermissionStatus;

    // Reset retry counter for manual requests
    globalTokenState.retryCount = 0;
    await globalLoadToken();
    return globalTokenState.notificationPermissionStatus;
};

const useFcmToken = () => {
    const router = useRouter();
    
    // Use local state that syncs with global state
    const [notificationPermissionStatus, setNotificationPermissionStatus] = useState(globalTokenState.notificationPermissionStatus);
    const [token, setToken] = useState(globalTokenState.token);
    const [isLoading, setIsLoading] = useState(globalTokenState.isLoading);
    const [error, setError] = useState(globalTokenState.error);
    
    // Subscribe to global state changes
    useEffect(() => {
        console.log('🔗 FCM Hook: New instance subscribing to global state');
        const unsubscribe = addSubscriber((state) => {
            setNotificationPermissionStatus(state.notificationPermissionStatus);
            setToken(state.token);
            setIsLoading(state.isLoading);
            setError(state.error);
        });
        
        // Initialize if not already done
        if (!globalTokenState.isInitialized && !isGloballyLoading) {
            console.log('🚀 FCM Hook: Initializing global token state...');
            
            if (isNotificationSupported()) {
                // Check if we have a stored token first
                const storedToken = getStoredToken();
                console.log('💾 FCM Hook: Stored token found:', storedToken ? 'Yes' : 'No');

                if (storedToken && Notification.permission === 'granted') {
                    updateGlobalState({
                        token: storedToken,
                        notificationPermissionStatus: 'granted',
                        isInitialized: true
                    });
                    console.log('✅ FCM Hook: Restored FCM token from storage');
                } else {
                    console.log('🚀 FCM Hook: Loading new token...');
                    globalLoadToken();
                }
            } else {
                console.log('❌ FCM Hook: Notifications not supported in this browser');
                updateGlobalState({
                    error: 'Notifications not supported in this browser',
                    isInitialized: true
                });
            }
        }
        
        return unsubscribe;
    }, []);

    // Helper function to handle notification clicks
    const handleNotificationClick = useCallback((event, url) => {
        event.preventDefault();
        event.target.close();

        // Focus the window
        if (window.focus) {
            window.focus();
        }

        if (url) {
            router.push(url);
        }
    }, [router]);

    // Helper function to handle toast action clicks
    const handleToastActionClick = useCallback((url) => {
        router.push(url);
    }, [router]);

    // Helper function to show notification
    const showBrowserNotification = useCallback((title, body, link, payload = null) => {
        if (Notification.permission !== 'granted') return;

        const notification = new Notification(
            title || "CubeNation Store",
            {
                body: body || "You have a new notification",
                icon: '/favicon.png',
                badge: '/favicon.png',
                tag: 'cubenation-notification',
                data: { 
                    url: link,
                    payload: payload // Store payload for analytics
                },
                requireInteraction: false,
                silent: false,
                timestamp: Date.now(),
            }
        );

        notification.onclick = async (event) => {
            // Track notification click before handling
            if (payload) {
                await trackNotificationClicked(payload);
            }
            handleNotificationClick(event, link);
        };
        
        // Auto-close notification after 5 seconds
        setTimeout(() => {
            notification.close();
        }, 5000);
    }, [handleNotificationClick]);

    // Helper function to handle FCM messages
    const handleFcmMessage = useCallback(async (payload) => {
        if (Notification.permission !== "granted") return;

        console.log("Foreground push notification received:", payload);
        
        // Track notification received in analytics
        await trackNotificationReceived(payload);
        
        const link = payload.fcmOptions?.link || payload.data?.link;
        const title = payload.notification?.title;
        const body = payload.notification?.body;
        const message = `${title}: ${body}`;

        // Show toast notification with action button if link is available
        if (link) {
            const handleToastClick = () => {
                // Track click when toast is clicked
                trackNotificationClicked(payload);
                handleToastActionClick(link);
            };
            const toastAction = {
                label: "Visit",
                onClick: handleToastClick,
            };
            notifySuccess(message, { action: toastAction });
        } else {
            notifySuccess(message);
        }

        // Also show browser notification for better visibility
        showBrowserNotification(title, body, link, payload);
    }, [handleToastActionClick, showBrowserNotification]);

    // Setup message listener
    useEffect(() => {
        console.log('🔍 FCM Hook: Message listener effect triggered. Token:', token ? 'Available' : 'Not available');

        if (!token) {
            console.log('⏸️ FCM Hook: No token available, skipping message listener setup');
            return;
        }

        let unsubscribe = null;

        const setupListener = async () => {
            try {
                console.log(`🎯 FCM Hook: FCM message listener registered with token: ${token.substring(0, 20)}...`);
                const messagingInstance = await messaging();
                if (!messagingInstance) {
                    console.warn('⚠️ FCM Hook: Firebase messaging not available');
                    return;
                }

                console.log('✅ FCM Hook: Firebase messaging instance obtained, setting up listener...');
                // Register a listener for incoming FCM messages
                unsubscribe = onMessage(messagingInstance, handleFcmMessage);
                console.log('✅ FCM Hook: Message listener successfully registered');

            } catch (error) {
                console.error('❌ FCM Hook: Error setting up FCM message listener:', error);
            }
        };

        setupListener();

        // Cleanup the listener when the component unmounts
        return () => {
            console.log('🧹 FCM Hook: Cleaning up message listener');
            if (unsubscribe) {
                unsubscribe();
            }
        };
    }, [token, router, handleToastActionClick, showBrowserNotification, handleFcmMessage]);

    return {
        fcmToken: token,
        notificationPermissionStatus,
        requestPermission: globalRequestPermission,
        isLoading,
        error,
        isSupported: isNotificationSupported(),
    };
};

export default useFcmToken;
